import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { CUIText } from '@dotcom/crown-ui';
import { useDebouncedCallback } from 'use-debounce';
import { FormValues, RangeProps, RangeVariants } from './range-types';
import styles from './range.module.scss';

export function Range({
  className = '',
  domId,
  isDisabled,
  label,
  max,
  min,
  onChange,
  options,
  register,
  showValueInLabel,
  step = 1,
  required,
  testId = 'range-testid',
  tickCount = 2,
  leftLabel,
  rightLabel,
  variant = RangeVariants.Primary,
  initialValue,
  enableTickInteraction,
  setValue,
  useDebounce = true,
}: RangeProps): React.ReactElement {
  const [rangeValue, setRangeValue] = useState(initialValue ?? min);
  const effectiveMin = min ?? 0;
  const effectiveMax = max ?? 100;

  const containerClasses = classNames(styles['input-range-container'], className);
  const classes = classNames(styles['input-range-field'], {
    [styles['secondary-input-range-field']]: variant === RangeVariants.Secondary,
  });
  const labelTextClasses = classNames(styles['input-label-text']);
  const labelSuffixClasses = classNames(styles['input-label-suffix']);
  const inputRangeSliderBackgroundClass = classNames({
    [styles['input-range-slider-background']]: variant === RangeVariants.Primary,
    [styles['secondary-input-range-slider-background']]: variant === RangeVariants.Secondary && tickCount === 0,
    [styles['secondary-input-range-slider-background-tick']]: variant === RangeVariants.Secondary && tickCount !== 0,
  });

  const inputRangeTickContainerClass = classNames({
    [styles['input-range-tick-container']]: variant === RangeVariants.Primary,
    [styles['secondary-input-range-tick-container']]: variant === RangeVariants.Secondary,
    [styles['enable-tick-interaction']]: enableTickInteraction,
  });

  const minInput = options ? '0' : min;
  const maxInput = options ? `${options.length - 1}` : max;

  const thumbPosition = useMemo(() => {
    const percentage = (((rangeValue ?? effectiveMin) - effectiveMin) / (effectiveMax - effectiveMin)) * 100;
    return `${percentage}%`;
  }, [rangeValue, min, max]);

  /**
   * State used to update the dynamic label with the current value
   */
  const [suffixText, setSuffixText] = useState<string>();

  /**
   * Range change handler. Fires the onChange handler containing the current value.
   */
  const onRangeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const parsedValue = parseInt(value, 10);
      setRangeValue(parsedValue);
      onChange?.(parsedValue);
      if (showValueInLabel && options) {
        setSuffixText(options[parsedValue as number]);
      }
    },
    [onChange, options, showValueInLabel],
  );

  /**
   * 250ms wait time to fire handler
   */

  const debouncedHandleOnChange = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    onRangeChange(event);
  }, 250);

  const handleInputChange = useDebounce ? debouncedHandleOnChange : onRangeChange;

  const onTickClick = (tickIndex: number) => {
    if (!enableTickInteraction) return;

    const perTick = (effectiveMax - effectiveMin) / (tickCount - 1);

    const value = effectiveMin + perTick * tickIndex;
    setRangeValue(value);
    onChange?.(value);
    setValue?.(domId as keyof FormValues, value);
  };

  const progressWidth = min && max && rangeValue && ((rangeValue - min) / (max - min)) * 100;

  return (
    <div className={containerClasses} data-testid={testId}>
      {label && (
        <CUIText as="label" variant="form" className={styles['input-label']} htmlFor={domId} required={required}>
          <span className={labelTextClasses}>{label}</span>{' '}
          {suffixText && <span className={labelSuffixClasses}>{suffixText}</span>}
        </CUIText>
      )}
      {variant === RangeVariants.Secondary && leftLabel && rightLabel && (
        <label htmlFor={domId} className={styles['input-range-labels']}>
          {leftLabel && <span className={styles['label-left']}>{leftLabel}</span>}
          {rightLabel && <span className={styles['label-right']}>{rightLabel}</span>}
        </label>
      )}
      <div className={inputRangeSliderBackgroundClass} />
      {variant === RangeVariants.Secondary && (
        <div className={styles['input-range-progress']} style={{ width: `${progressWidth}%` }} />
      )}
      {/* Range ticks */}
      {tickCount > 0 && (
        <div className={inputRangeTickContainerClass}>
          {Array.from(Array(tickCount).keys()).map(
            (item: number): React.ReactElement => (
              <div className={styles['input-range-tick']} key={`tick-${item}`} onClick={() => onTickClick(item)} /> // eslint-disable-line
            ),
          )}
        </div>
      )}
      {enableTickInteraction && (
        <div className={styles['custom-thumb-container']}>
          <div className={styles['custom-thumb']} style={{ left: thumbPosition }}></div>
        </div>
      )}
      <input
        className={`${classes} ${enableTickInteraction ? styles['custom-thumb-enabled'] : ''}`}
        disabled={isDisabled}
        id={domId}
        max={maxInput}
        min={minInput}
        step={step}
        type="range"
        {...register}
        onInput={handleInputChange}
      />
    </div>
  );
}
